import { z } from 'zod';
import { contentIdSchema } from './common.js';

/**
 * Contrato del programa público.
 *
 * Es la respuesta a lo primero que ve alguien que abre el dominio, así que hay
 * que poder cachearla y no depende de quién pregunte. Por eso no lleva NADA de
 * la cuenta: ni progreso, ni nombre, ni estado. Dos personas que llamen al
 * mismo endpoint reciben byte a byte lo mismo, y por eso la respuesta pública
 * puede ser cacheable y no puede filtrar datos de nadie.
 *
 * `disponible` va aquí y no en el contenido por una razón concreta: el frontend
 * lo usa para pintar "Próximamente" y en un mes que ya está escrito puede pasar
 * de false a true sin tocar ni el schema ni los tipos.
 */

export const programWeekSchema = z.object({
  week: z.number().int().min(1).max(4),
  /** El tema de la semana en una línea, en lenguaje de alumno. */
  topic: z.string().min(1),
  /** Cuántas lecciones escritas hay en esa semana. 0 si el mes aún no está. */
  lessonCount: z.number().int().min(0),
  /**
   * Enlace a la primera lección de la semana, o null si esa semana no tiene
   * nada escrito. Va en la semana y no en el mes porque es la semana la que se
   * puede abrir: un botón "empezar" que lleva al mes entero obliga a elegir
   * lección y el alumno no sabe cuál.
   */
  firstLessonId: contentIdSchema.nullable(),
});

export const programMonthSchema = z.object({
  month: z.number().int().min(1).max(12),
  /**
   * Nivel pedagógico (0..6) al que contribuye el mes. El mes agrupa semanas en
   * el tiempo; el nivel dice qué se necesita saber antes. Opcional porque los
   * meses escritos antes de existir el campo no lo traen.
   */
  nivel: z.number().int().min(0).max(6).optional(),
  title: z.string().min(1),
  summary: z.string().min(1),
  skills: z.array(z.string().min(1)).min(1),
  weeks: z.array(programWeekSchema).min(1),
  /**
   * ¿Hay lecciones escritas de este mes? Si es false, las semanas aparecen sin
   * enlace y con la etiqueta "Próximamente": es preferible a un enlace que
   * lleva a un temario vacío.
   */
  disponible: z.boolean(),
  /** Proyecto del mes, si lo tiene. Vive en `content/portfolio`. */
  projectTitle: z.string().nullable(),
  /** Total de lecciones escritas del mes. */
  lessonCount: z.number().int().min(0),
  exerciseCount: z.number().int().min(0),
});

export const programResponseSchema = z.object({
  meta: z.object({
    title: z.string().min(1),
    subtitle: z.string().min(1),
    level: z.string().min(1),
    horasSemanales: z.number().int().min(1),
    mesesTotales: z.number().int().min(1),
    proyectosTotales: z.number().int().min(1),
    /** Lecciones escritas ahora mismo, en todos los meses. */
    lessonCount: z.number().int().min(0),
  }),
  months: z.array(programMonthSchema).min(1),
});

/**
 * @typedef {import('zod').infer<typeof programMonthSchema>} ProgramMonth
 * @typedef {import('zod').infer<typeof programResponseSchema>} ProgramResponse
 */

// ─── Formato de los archivos de contenido ────────────────────────────────────

/**
 * `content/programa/index.js` también se valida, por la misma razón que el
 * temario: si alguien añade el mes 8 sin `weeks` o con una semana 9, que se entere
 * al arrancar el servidor y no al pintar la portada para un visitante.
 *
 * Es un schema aparte del de la respuesta porque el archivo y la respuesta no son
 * lo mismo: el archivo no sabe cuántas lecciones hay escritas, eso se cuenta.
 */

const programFileWeekSchema = z.object({
  week: z.number().int().min(1).max(4),
  topic: z.string().min(1),
});

const programFileMonthSchema = z.object({
  month: z.number().int().min(1).max(12),
  nivel: z.number().int().min(0).max(6).optional(),
  title: z.string().min(1),
  summary: z.string().min(1),
  skills: z.array(z.string().min(1)).min(1),
  weeks: z.array(programFileWeekSchema).min(1),
  disponible: z.boolean(),
  projectKey: z.string().min(1).optional(),
});

export const programCatalogSchema = z.array(programFileMonthSchema).min(1);

/**
 * Que los números de mes sean 1, 2, 3... y no 1, 1, 4.
 *
 * Sin esto, un mes duplicado no rompe nada: aparece dos veces en la portada y
 * nadie sabe si es un error o si el temario tiene dos partes. La comprobación es
 * una línea y evita esa discusión para siempre.
 *
 * @param {import('zod').infer<typeof programCatalogSchema>} catalogo
 */
export function programaSinMesesRepetidos(catalogo) {
  const vistos = new Set();

  for (const mes of catalogo) {
    if (vistos.has(mes.month)) {
      throw new Error(`El programa define dos veces el mes ${mes.month}.`);
    }
    vistos.add(mes.month);
  }

  return catalogo;
}
